import { Button, ColumnTable, Icon, Table, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';

import DisplayError from '../../components/info/DisplayError';
import Loading from '../../components/info/Loading';
import Tooltip from '../../components/tooltip/Tooltip';
import { TranslationsKeys } from '../../locales/translations';
import { downloadFromBlob, readErrorMessageFromBlob } from '../../utils/blob';
import { downloadTemplate, getBenefitAllocationForm } from './api';
import AllocationHistoryModal from './parts/AllocationHistoryModal';
import InvalidFormatModal from './parts/InvalidFormatModal';
import UploadBenefitModal from './parts/UploadBenefitModal';
import UploadFailureModal from './parts/UploadFailureModal';
import {
  BenefitAllocationForm,
  BenefitAllocationKind,
  BenefitKind,
} from './types';

export default function BenefitAllocation() {
  const { t } = useTranslation(
    TranslationsKeys.ADMISSION_AND_ENROLLMENTS_BENEFIT_ALLOCATION,
  );

  const [form, setForm] = useState<BenefitAllocationForm | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const [
    downloadingCode,
    setDownloadingCode,
  ] = useState<BenefitAllocationKind | null>(null);
  const [uploadKind, setUploadKind] = useState<BenefitKind | null>(null);
  const [historyKind, setHistoryKind] = useState<BenefitKind | null>(null);
  const [showInvalidFormat, setShowInvalidFormat] = useState<boolean>(false);
  const [failureId, setFailureId] = useState<number | string | null>(null);

  const loadForm = useCallback(async () => {
    setLoading(true);
    setErrorCode(null);
    const { data, error } = await getBenefitAllocationForm();
    if (error) {
      setErrorCode(error.code);
    } else {
      setForm(data ?? null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  const handleDownloadTemplate = useCallback(
    async (code: BenefitAllocationKind) => {
      setDownloadingCode(code);
      const res = await downloadTemplate(code);
      if (res.error) {
        const message = await readErrorMessageFromBlob(res.error.data);
        addToast({
          icon: 'error',
          color: 'danger',
          text: message ?? t('errors.generic'),
        });
      } else {
        const blob = new Blob([res.data], { type: 'vnd.ms-excel' });
        downloadFromBlob(blob, `Plantilla_Asignacion_${code}.xlsx`);
      }
      setDownloadingCode(null);
    },
    [t],
  );

  const handleUploadSuccess = useCallback(() => {
    setUploadKind(null);
    addToast({ icon: 'success', color: 'success', text: t('success') });
  }, [t]);

  const handleInvalidFormat = useCallback(() => {
    setUploadKind(null);
    setShowInvalidFormat(true);
  }, []);

  const handleInvalidData = useCallback((id: number | string) => {
    setUploadKind(null);
    setFailureId(id);
  }, []);

  const columns = useMemo<ColumnTable<BenefitKind>[]>(
    () => [
      {
        columnName: 'name',
        headerText: t('table.report'),
        // Los anchos fijan el mínimo de la tabla: sin ellos el navegador comprime
        // las columnas y el wrapper responsive de global-ui nunca activa el scroll.
        width: '220px',
        thClassName: 'pl-4',
        tdClassName: 'pl-4',
        cellFormat: ({ row }) => t(`kinds.${row.code}.name`),
      },
      {
        columnName: 'description',
        headerText: t('table.description'),
        width: '340px',
        cellFormat: ({ row }) => t(`kinds.${row.code}.description`),
      },
      {
        columnName: 'history',
        headerText: t('table.history'),
        width: '180px',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <Tooltip
            id={`benefit-allocation-history-${row.code}`}
            text={t('table.viewHistory')}
            placement="top"
          >
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setHistoryKind(row)}
            >
              <Icon name="classes_history" color="primary" size="24px" />
            </button>
          </Tooltip>
        ),
      },
      {
        columnName: 'actions',
        headerText: t('table.action'),
        width: '300px',
        thClassName: 'text-right pr-5',
        tdClassName: 'text-right pr-4',
        cellFormat: ({ row }) => (
          <div className="d-flex justify-content-end flex-nowrap">
            <Button
              size="sm"
              outlined
              className="mr-2 mb-1"
              text={t('table.downloadTemplate')}
              loading={downloadingCode === row.code}
              disabled={downloadingCode !== null}
              onClick={() => handleDownloadTemplate(row.code)}
            />
            <Button
              size="sm"
              className="mb-1"
              text={t('table.upload')}
              onClick={() => setUploadKind(row)}
            />
          </div>
        ),
      },
    ],
    [t, downloadingCode, handleDownloadTemplate],
  );

  if (loading) {
    return (
      <div className="mx-3">
        <Loading insideCard />
      </div>
    );
  }

  if (errorCode || !form) {
    return (
      <div className="mx-3">
        <DisplayError
          insideCard
          textBody={errorCode ?? 'HTTP_ERROR'}
          retryAction={loadForm}
          loadingAction={loading}
        />
      </div>
    );
  }

  return (
    <div className="mx-3">
      <Card className="p-4">
        <h1 className="fs-20 text-primary fw-700">{t('title')}</h1>
        <p className="fs-16 mb-4">{t('description')}</p>
        <Table columns={columns} data={form.benefitKinds} />
      </Card>

      <UploadBenefitModal
        isOpen={!!uploadKind}
        benefitKind={uploadKind}
        academicYears={form.academicYears}
        onClose={() => setUploadKind(null)}
        onSuccess={handleUploadSuccess}
        onInvalidFormat={handleInvalidFormat}
        onInvalidData={handleInvalidData}
      />
      <AllocationHistoryModal
        isOpen={!!historyKind}
        benefitKind={historyKind}
        onClose={() => setHistoryKind(null)}
      />
      <InvalidFormatModal
        isOpen={showInvalidFormat}
        onClose={() => setShowInvalidFormat(false)}
      />
      <UploadFailureModal
        isOpen={!!failureId}
        fileUploadFailureId={failureId}
        onClose={() => setFailureId(null)}
      />
    </div>
  );
}
